import PageHeader from 'components/general/pageHeader';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet } from 'services';
import { Loading, SystemText, Forms } from 'e4shared';
import { setPrismMenu } from 'actions/ui';
import { EmptyState } from './components/emptyState';
import Recommended from './components/recommended';
import ExploreAll from './components/exploreAll';
import Completed from './components/completed';
import InProgress from './components/inProgress';
import { translateCompetency, translateCourse } from 'helpers/translations';

export const Library = () => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);   
    const { user } = useSelector(state => state.user);

    React.useEffect(() => {
        dispatch(setPrismMenu('Library'));        
    }, []);

    React.useEffect(() => {
        if (user?.guid) {            
            loadLibraryData();
        }
    }, [user?.guid]);

    const loadLibraryData = () => {
        setLoading(true);
        setError(false);

        apiGet(
            `library/${user.id}`,
            user.guid,
            response => {
                const responseData = response || {};
                
                if (responseData.inProgressCourses) {
                    responseData.inProgressCourses = responseData.inProgressCourses.map(c => translateCourse(user.language_Code, c));
                }
                if (responseData.recommendedBehaviours) {
                    responseData.recommendedBehaviours = responseData.recommendedBehaviours.map(b => translateCompetency(user.language_Code, b));
                }
                if (responseData.completedCourses) {
                    responseData.completedCourses = responseData.completedCourses.map(c => translateCourse(user.language_Code, c));
                }
                if (responseData.exploreAllBehaviours) {
                    responseData.exploreAllBehaviours = responseData.exploreAllBehaviours.map(b => translateCompetency(user.language_Code, b));
                }
                
                setData(responseData);
                setLoading(false);
            },
            err => {
                console.error('Error loading library data:', err);
                setError(true);
                setLoading(false);
            }
        );
    };

    const text = React.useMemo(
        () => ({
            title: SystemText('Library/Heading/Title'),
            description: SystemText('Library/Description'),
            noResults: SystemText('Library/NoResults/Title'),
            noResultsSummary: SystemText('Library/NoResults/Summary'),
            error: SystemText('Library/Error/Title'),
            errorSummary: SystemText('Library/Error/Summary'),
            retry: SystemText('Library/Error/Retry'),
            search: SystemText('Library/Search/Label'),
            categoryFilter: SystemText('Library/Filter/Category'),
            sortByTitle: SystemText('Library/Sort/Title'),
            sortByCategory: SystemText('Library/Sort/Category'),
            sortByDate: SystemText('Library/Sort/Date'),
            groupByCategory: SystemText('Library/Toggle/GroupByCategory'),
            refresh: SystemText('Library/Buttons/Refresh'),
            inprogress: SystemText('Library/Heading/InProgress'),
            recommended: SystemText('Library/Heading/Recommended'),
            recommendedSummary: SystemText('Library/Summary/Recommended'),
            completed: SystemText('Library/Heading/Completed'),
            explore: SystemText('Library/Heading/ExploreAll'),
        }),
        []
    );

    return (
        <div id="library-page" data-testid="library-page">
            <PageHeader title={text.title} />

            {data.inProgressCourses?.length > 0 && (
                <div className="mt30">
                    <InProgress text={text} data={data.inProgressCourses} />
                </div>
            )}

            {data.recommendedBehaviours?.length > 0 && (
                <div className="mt30">
                    <Recommended text={text} data={data.recommendedBehaviours} />
                </div>
            )}

            {data.completedCourses?.length > 0 && (
                <div className="mt30">
                    <Completed text={text} data={data.completedCourses} />
                </div>
            )}
           
            {data.exploreAllBehaviours?.length > 0 && (
                <div className="mt30">
                    <ExploreAll text={text} data={data.exploreAllBehaviours} />
                </div>
            )}

            <section className="content mtm10">
                {loading && <Loading />}
                {error && (
                    <EmptyState
                        icon="exclamation-triangle"
                        title={text.error}
                        message={text.errorSummary}
                        action={
                            <button className="btn btn-primary" onClick={loadLibraryData}>
                                {text.retry}
                            </button>
                        }
                    />
                )}
            </section>
        </div>
    );
};

export default Library;
